import type { ExpenseCategory } from '../../../types';
import {
  BedDouble, Plane, Utensils, Sparkles, Bus, Banknote, Package, Fuel, SquareParking,
} from '../../ui/Icon';
import type { LucideIcon } from '../../ui/Icon';

/** Category → colour. The ONE colour map in the web client: the ledger, its
 *  filter pills, the day chart and the donut all read it, so a category keeps
 *  the same colour wherever it shows up. Mirrors
 *  `src/components/expenses/meta.ts` in gromly-mobile.
 *
 *  Typed as a total Record on purpose. It used to be `Record<string, string>`,
 *  and adding FUEL/PARKING to the enum left them with no colour and nothing
 *  failing: the ledger's active filter pill paints `text-white` over this
 *  background, so the label of the category you had just filtered by turned
 *  white on white and vanished. */
export const CAT_HEX: Record<ExpenseCategory, string> = {
  ACCOMMODATION: '#6366f1',
  FLIGHTS:       '#0ea5e9',
  FOOD:          '#f97316',
  ACTIVITIES:    '#14b8a6',
  TRANSPORT:     '#64748b',
  FUEL:          '#eab308',
  PARKING:       '#ec4899',
  CASH:          '#16a34a',
  OTHER:         '#94a3b8',
};

/** The colour of a category that may not be one — a grouping key, a value read
 *  off the wire. Never returns undefined, which is what an inline style needs. */
export function catHex(category: string): string {
  return CAT_HEX[category as ExpenseCategory] ?? CAT_HEX.OTHER;
}

/** Category → icon. Total for the same reason as CAT_HEX: the community trip
 *  page kept its own open map without FUEL/PARKING, and both fell back to
 *  OTHER — three "Other" rows in one budget. */
export const CAT_ICON: Record<ExpenseCategory, LucideIcon> = {
  ACCOMMODATION: BedDouble,
  FLIGHTS:       Plane,
  FOOD:          Utensils,
  ACTIVITIES:    Sparkles,
  TRANSPORT:     Bus,
  FUEL:          Fuel,
  PARKING:       SquareParking,
  CASH:          Banknote,
  OTHER:         Package,
};

export function catIcon(category: string): LucideIcon {
  return CAT_ICON[category as ExpenseCategory] ?? CAT_ICON.OTHER;
}
